import { create } from "zustand";
import { mirroredStore } from "@/lib/mirrored-store";
import type { ScreenshotScreenMeta } from "@/lib/pointing-transform";

export interface StoredOutboundScreens {
  screens: ScreenshotScreenMeta[];
  registeredAt: number;
}

export interface OutboundScreenshotState {
  lastScreens: StoredOutboundScreens | null;
  screensByRequestId: Array<[string, StoredOutboundScreens]>;
  screensByImageId: Array<[string, StoredOutboundScreens]>;
  screensByExecutionId: Array<[string, StoredOutboundScreens]>;
  screensBySessionId: Array<[string, StoredOutboundScreens]>;
  pendingDispatches: StoredOutboundScreens[];
}

// Prompt screenshots may originate on a mirror while presentations arrive on
// every display. Keep their exact coordinate grids in canonical desktop state.
export const useOutboundScreenshotStore = create<OutboundScreenshotState>()(
  mirroredStore<OutboundScreenshotState>("outboundScreens", () => ({
    lastScreens: null,
    screensByRequestId: [],
    screensByImageId: [],
    screensByExecutionId: [],
    screensBySessionId: [],
    pendingDispatches: [],
  })),
);

const registryLimits = {
  screensByImageId: 128,
  screensByExecutionId: 64,
  screensBySessionId: 64,
  screensByRequestId: 64,
} as const;

// Independent displays publish patches from different snapshots. Merge bound
// identities instead of allowing one display's registry to replace another's.
export function applyOutboundScreenshotPatch(
  patch: Partial<OutboundScreenshotState>,
): void {
  useOutboundScreenshotStore.setState((state) => {
    const merged = { ...patch };
    for (const key of Object.keys(registryLimits) as Array<
      keyof typeof registryLimits
    >) {
      const incoming = patch[key];
      if (!incoming || incoming.length === 0) continue;
      const entries = new Map(state[key]);
      for (const [id, screens] of incoming) {
        const current = entries.get(id);
        if (!current || screens.registeredAt >= current.registeredAt) {
          entries.set(id, screens);
        }
      }
      merged[key] = [...entries]
        .sort((a, b) => a[1].registeredAt - b[1].registeredAt)
        .slice(-registryLimits[key]);
    }
    if (
      patch.lastScreens &&
      state.lastScreens &&
      patch.lastScreens.registeredAt < state.lastScreens.registeredAt
    ) {
      merged.lastScreens = state.lastScreens;
    }
    return merged;
  });
}
